import './Gallery.css';

import Button from 'components/Button/Button';
import LoadingImage from 'components/LoadingImage/LoadingImage';
import * as React from 'react';
import Lightbox from 'react-image-lightbox';
import * as Markdown from 'react-markdown';
import Placeholder from 'react-placeholder';
import Slider, { Settings } from 'react-slick';
import styled from 'styled-components';
import Section from 'wrappers/Section/Section';

interface IGalleryItem {
  src: string;
  name?: string;
  subtitle?: string;
  buttonText?: string;
  buttonLink?: string;
}

interface IProps {
  loading?: boolean;
  images: IGalleryItem[];
  lightbox?: boolean;
  height?: string;
  settings?: Settings;
  className?: string;
  maxHeight?: string;
  width?: string;
  overflow?: string;
}

interface IState {
  currentImage: number;
  lightboxOpen: boolean;
}

const defaultSettings: Settings = {
  dots: true,
  dotsClass: 'gallery-slider-dot',
  infinite: true,
  slidesToScroll: 1,
  slidesToShow: 1,
  speed: 500
};

export class Gallery extends React.Component<IProps, IState> {
  public state: IState = {
    currentImage: 0,
    lightboxOpen: false
  };

  public handleClose = () => {
    this.setState({
      lightboxOpen: false
    });
  };

  public openImage = (i: number) => () => {
    this.setState({
      currentImage: i,
      lightboxOpen: true
    });
  };

  public changeImage = (direction: 'next' | 'prev' = 'next') => () => {
    const modifier = direction === 'next' ? 1 : -1;
    if (this.state.currentImage + modifier >= this.props.images.length) {
      return this.setState({
        currentImage: 0
      });
    } else if (this.state.currentImage + modifier < 0) {
      return this.setState({
        currentImage: this.props.images.length - 1
      });
    } else {
      return this.setState({
        currentImage: this.state.currentImage + modifier
      });
    }
  };

  public render() {
    const height = this.props.height || 'initial';
    const slickSettings: Settings = {
      ...defaultSettings,
      ...this.props.settings
    };
    const getNextSrc = () => {
      if (this.state.currentImage < this.props.images.length) {
        return this.props.images[this.state.currentImage].src;
      }
      return this.props.images[0].src;
    };

    const getPrevSrc = () => {
      if (this.state.currentImage === 0) {
        return this.props.images[this.props.images.length - 1].src;
      }
      return this.props.images[this.state.currentImage - 1].src;
    };

    return (
      <Container
        overflow={this.props.overflow}
        className={this.props.className}
        height={height}
      >
        <Placeholder
          showLoadingAnimation={true}
          type="rect"
          ready={!this.props.loading}
        >
          <Slider {...slickSettings}>
            {this.props.images.map((image, i) => (
              <ImageContainer
                number={
                  this.props.settings ? this.props.settings.slidesToShow : 1
                }
                key={image.src}
              >
                <StyledImage
                  imageheight={height}
                  maxheight={this.props.maxHeight}
                  src={image.src}
                  onClick={this.openImage(i)}
                  style={{ width: this.props.width || 'initial' }}
                  number={
                    this.props.settings ? this.props.settings.slidesToShow : 1
                  }
                />
                {image.name && (
                  <Content>
                    <StyledSection>
                      <Inner>
                        <div>
                          <Title>{image.name}</Title>
                          {image.subtitle && (
                            <Subtitle source={image.subtitle} />
                          )}
                          {image.buttonText && (
                            <StyledButton
                              link={image.buttonLink}
                              target={'_blank'}
                            >
                              {image.buttonText}
                            </StyledButton>
                          )}
                        </div>
                      </Inner>
                    </StyledSection>
                  </Content>
                )}
              </ImageContainer>
            ))}
          </Slider>
        </Placeholder>
        {this.props.lightbox && this.state.lightboxOpen && (
          <Lightbox
            mainSrc={this.props.images[this.state.currentImage].src}
            onCloseRequest={this.handleClose}
            nextSrc={getNextSrc()}
            prevSrc={getPrevSrc()}
            onMoveNextRequest={this.changeImage('next')}
            onMovePrevRequest={this.changeImage('prev')}
          />
        )}
      </Container>
    );
  }
}

const Container = styled.div<{ height: string; overflow?: string }>`
  height: ${p => p.height};
  overflow: ${p => (p.overflow ? p.overflow : 'initial')};
`;

const StyledImage = styled(LoadingImage)<{
  imageheight: string;
  number?: number;
  maxheight?: string;
}>`
  max-height: ${p => p.maxheight || p.imageheight};
  margin: 0 auto;
  width: ${p => (p.maxheight ? 'auto' : p.number === 2 ? '100%' : 'initial')};
`;

const ImageContainer = styled.div<{ number?: number }>`
  padding: ${p => (p.number === 2 ? '15px' : 0)};
  // color: white;
  // width: 50%;
  position: relative;
`;

const Content = styled.div`
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  display: flex;
  @media (max-width: 600px) {
    display: none;
  }
`;

const Title = styled.div`
  font-size: 51px;
  font-weight: 700;
  text-transform: uppercase;
  color: #371951;
`;

const StyledSection = styled(Section)`
  flex: 1;
`;

const Inner = styled.div`
  display: flex;
  justify-content: center;
  height: 100%;
  flex-direction: column;
`;

const Subtitle = styled(Markdown)`
  margin-top: 8px;
`;

const StyledButton = styled(Button)`
  margin-top: 30px;
`;

export default Gallery;



// WEBPACK FOOTER //
// ./src/components/Gallery/Gallery.tsx